<template>
  <div class="fin-screen">
    <div class="fin-toolbar">
      <h2 class="fin-title">{{ $t("finance_tab_transactions") }}</h2>
      <ui-segment
        :value="view"
        :options="viewOptions"
        :label="$t('finance_tab_transactions')"
        @input="shell.setTxView($event)"
      >
        <template #option="{ option }"
          >{{ option.label
          }}<span v-if="option.badge" class="ui-chip ui-chip--pill ui-chip--accent">{{
            option.badge
          }}</span></template
        >
      </ui-segment>
      <span class="fin-grow"></span>
      <select
        v-if="view === 'order'"
        v-model="scope"
        class="ui-input ui-input--select"
        :aria-label="$t('finance_shell.scope.label')"
      >
        <option value="all">{{ phone ? $t("finance_shell.filters.payment_all") : $t("finance_scope_all_long") }}</option>
        <option value="orders">{{ $t("finance_scope_orders") }}</option>
        <option value="payments">{{ $t("finance_scope_payments") }}</option>
      </select>
      <select
        v-if="view === 'order' && serviceOptions.length"
        v-model="serviceId"
        class="ui-input ui-input--select"
        :aria-label="$t('finance_service_label')"
      >
        <option :value="null">{{ $t("finance_service_label") }}</option>
        <option
          v-for="option in serviceOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <ui-search
        v-if="searchVisible"
        ref="search"
        v-model="search"
        class="fin-search"
        :placeholder="
          $t(
            view === 'refunds'
              ? 'finance_shell.search_refunds'
              : 'finance_search_transactions'
          )
        "
        :label="$t('search')"
      />
      <!-- telefon: lupa i izvoz su u zaglavlju stranice uz osvježi i štampu (phoneTools), a polje za pretragu se otvara tek na dodir -->
      <finance-export
        v-if="!phone"
        :filename="exportFilename"
        :get-data="getExportData"
        :disabled="exportDisabled"
        @print="shell.printPage"
      />
    </div>

    <finance-pending-bar v-if="view !== 'refunds'" :period="period" />

    <!-- Po transakciji -->
    <template v-if="view === 'order'">
      <div class="fin-filters">
        <ui-segment
          :value="paymentMethod"
          :options="paymentOptions"
          :label="$t('finance_shell.filters.payment_label')"
          @input="paymentMethod = $event"
        />
        <report-checklist-menu
          :label="$t('finance_shell.filters.type_label')"
          :items="entryTypeItems"
          :value="entryTypeValue"
          icon="mdi-filter-variant"
          @input="setEntryTypes"
        />
        <button
          v-if="filtersActive"
          type="button"
          class="ui-text-btn"
          @click="clearFilters"
        >
          {{ $t("finance_shell.filters.clear") }}
        </button>
      </div>
      <ui-banner v-if="paymentMethod" tone="info">{{
        $t("finance_shell.filters.payment_note")
      }}</ui-banner>
      <ui-banner v-if="entryTypeFiltered" tone="info">{{
        $t("finance_shell.filters.type_note")
      }}</ui-banner>

      <div v-if="summary" class="fin-summary">
        <span v-if="filtersActive" class="ui-chip ui-chip--accent">{{
          $t("finance_shell.filters.filtered")
        }}</span>
        <!-- uz filter tipa backend `order_count` računa samo nad izabranim redovima (0 kad nema ORDER_REVENUE), pa broj ne nudimo -->
        <span v-if="!entryTypeFiltered"
          >{{ $t("finance_summary_orders", { count: summary.orders }) }} ·
          {{ $t("finance_summary_payments", { count: paymentCount }) }}</span
        >
        <span
          >{{ $t("finance_turnover") }}
          <b class="fin-num">{{ money(summary.turnover) }}</b></span
        >
        <span
          >{{ $t("finance_shell.types.COMMISSION") }}
          <b class="fin-num">{{ money(summary.commission) }}</b></span
        >
        <span
          >{{ $t("finance_summary_cash_direct") }}
          <b class="fin-num">{{ money(summary.cash) }}</b></span
        >
      </div>

      <finance-state v-if="loading && !groups.length" kind="loading" :lines="4" />
      <finance-state
        v-else-if="failed"
        kind="error"
        icon="mdi-alert-circle-outline"
        :title="$t('finance_shell.error_title')"
        :text="$t('finance_transactions_load_error')"
      >
        <button type="button" class="ui-btn ui-btn--dark" @click="load(1)">
          <v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}
        </button>
      </finance-state>
      <finance-state
        v-else-if="!visibleGroups.length"
        kind="empty"
        :icon="search ? 'mdi-magnify' : 'mdi-swap-horizontal'"
        :title="
          search
            ? $t('finance_shell.no_search_results')
            : $t('finance_no_transactions_filtered')
        "
        :text="search ? $t('finance_shell.no_search_results_text') : ''"
      >
        <button v-if="search" type="button" class="ui-btn" @click="search = ''">
          {{ $t("finance_shell.clear_search") }}
        </button>
        <button v-if="filtersActive" type="button" class="ui-btn" @click="clearFilters">
          {{ $t("finance_shell.filters.clear") }}
        </button>
      </finance-state>

      <div v-else class="fin-list" :aria-busy="String(loading)">
        <component
          :is="group.isOrder ? 'button' : 'div'"
          v-for="group in visibleGroups"
          :key="group.key"
          class="fin-tx"
          :class="[
            `is-${group.tone}`,
            { 'is-click': group.isOrder, 'is-on': activeKey === group.key },
          ]"
          :type="group.isOrder ? 'button' : null"
          @click="group.isOrder && openOrder(group)"
        >
          <span class="fin-tx__head">
            <template v-if="group.isOrder">
              <span class="fin-tx__id">#{{ group.sourceId }}</span>
              <span class="fin-tx__date">{{ dateTime(group.date) }}</span>
            </template>
            <template v-else>
              <span class="fin-tx__title">{{ eventTitle(group) }}</span>
              <span class="fin-tx__date">{{ localeDate(group.date) }}</span>
            </template>
            <span
              v-for="chip in chips(group)"
              :key="chip.text"
              class="ui-chip"
              :class="chip.tone"
              >{{ chip.text }}</span
            >
            <span class="fin-tx__net fin-num" :class="`is-${group.tone}`">
              <small>{{
                group.isOrder
                  ? entryTypeFiltered
                    ? $t("finance_shell.filters.selected_sum")
                    : $t("finance_net_for_you")
                  : eventCaption(group)
              }}</small>
              {{ money(group.net, true) }}
            </span>
          </span>
          <span class="fin-tx__lines">
            <span v-for="line in group.lines" :key="line.id">
              {{ lineLabel(line) }}
              <b
                class="fin-num"
                :class="
                  line.isCash
                    ? 'is-cash'
                    : line.signed < 0
                    ? 'is-out'
                    : line.isRevenue
                    ? ''
                    : 'is-in'
                "
                >{{ money(line.signed, !line.isRevenue) }}</b
              >
            </span>
          </span>
        </component>
      </div>

      <nav v-if="pageRange" class="fin-pager">
        <template v-if="lastPage > 1">
          <button
            type="button"
            class="ui-btn ui-btn--sm"
            :disabled="page <= 1 || loading"
            :aria-label="$t('finance_shell.page_prev')"
            @click="load(page - 1)"
          >
            <v-icon size="16">mdi-chevron-left</v-icon>
          </button>
          <template v-for="(item, index) in pageButtons">
            <span
              v-if="item === null"
              :key="`gap-${index}`"
              class="fin-pager__gap"
              aria-hidden="true"
              >…</span
            >
            <button
              v-else
              :key="item"
              type="button"
              class="ui-btn ui-btn--sm"
              :class="{ 'ui-btn--dark': item === page }"
              :aria-current="item === page ? 'page' : null"
              :disabled="loading"
              @click="item !== page && load(item)"
            >
              {{ item }}
            </button>
          </template>
          <button
            type="button"
            class="ui-btn ui-btn--sm"
            :disabled="page >= lastPage || loading"
            :aria-label="$t('finance_shell.page_next')"
            @click="load(page + 1)"
          >
            <v-icon size="16">mdi-chevron-right</v-icon>
          </button>
        </template>
        <small class="ui-muted">{{ pageRange }}</small>
      </nav>
    </template>

    <!-- Izvod -->
    <template v-else-if="view === 'ledger'">
      <finance-state v-if="loading && !ledger.length" kind="loading" :lines="4" />
      <finance-state
        v-else-if="failed"
        kind="error"
        icon="mdi-alert-circle-outline"
        :title="$t('finance_shell.error_title')"
        :text="$t('finance_balance_ledger_load_error')"
      >
        <button type="button" class="ui-btn ui-btn--dark" @click="loadLedger">
          <v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}
        </button>
      </finance-state>
      <section v-else class="ui-card fin-table-card" :aria-busy="String(loading)">
        <div class="ui-table-wrap ui-table-wrap--tall">
          <table class="ui-table">
            <thead>
              <tr>
                <th>{{ $t("finance_date") }}</th>
                <th>{{ $t("finance_description") }}</th>
                <th>{{ $t("finance_direction") }}</th>
                <th class="r">{{ $t("finance_amount") }}</th>
                <th class="r">{{ $t("finance_balance") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td></td>
                <td>
                  <b>{{ $t("finance_opening_balance") }}</b>
                </td>
                <td></td>
                <td></td>
                <td class="r fin-num">
                  <b>{{ money(opening) }}</b>
                </td>
              </tr>
              <tr v-for="entry in ledger" :key="entry.id">
                <td>{{ localeDate(entry.date) }}</td>
                <td>{{ translate(entry.description) }}</td>
                <td>
                  <span v-if="flow(entry.moneyFlow)" class="fin-flow"
                    ><i :style="{ background: flow(entry.moneyFlow).color }"></i
                    >{{ $t(flow(entry.moneyFlow).labelKey) }}</span
                  >
                </td>
                <td class="r fin-num" :class="entry.amount < 0 ? 'is-out' : 'is-in'">
                  <b>{{ money(entry.amount, true) }}</b>
                </td>
                <td class="r fin-num">
                  <b>{{ entry.balance === null ? "—" : money(entry.balance) }}</b>
                </td>
              </tr>
              <tr v-if="!ledger.length">
                <td colspan="5" class="ui-muted fin-center">
                  {{
                    search
                      ? $t("finance_shell.no_search_results")
                      : $t("finance_no_transactions_filtered")
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- Refundacije -->
    <template v-else>
      <form class="fin-newref" @submit.prevent="newRefund">
        <label for="fin-newref">{{ $t("finance_request_refund") }}</label>
        <input
          id="fin-newref"
          v-model="newOrderId"
          class="ui-input"
          inputmode="numeric"
          :placeholder="$t('finance_shell.order.order_number')"
          autocomplete="off"
        />
        <button type="submit" class="ui-btn" :disabled="!validNewOrder">
          {{ $t("finance_shell.order.open_order") }}
        </button>
      </form>

      <finance-state v-if="loading" kind="loading" :lines="3" />
      <finance-state
        v-else-if="failed"
        kind="error"
        icon="mdi-alert-circle-outline"
        :title="$t('finance_shell.error_title')"
        :text="$t('finance_refunds_load_error')"
      >
        <button type="button" class="ui-btn ui-btn--dark" @click="loadRefunds">
          <v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}
        </button>
      </finance-state>
      <finance-state
        v-else-if="!visibleRefunds.length"
        kind="empty"
        icon="mdi-cash-refund"
        :title="$t('finance_shell.no_refunds')"
      />
      <div v-else class="fin-list">
        <button
          v-for="refund in visibleRefunds"
          :key="refund.id"
          type="button"
          class="fin-tx is-click"
          :class="refund.status === 'pending' ? 'is-wait' : 'is-in'"
          @click="openRefund(refund)"
        >
          <span class="fin-tx__head">
            <span class="fin-tx__id">#{{ refund.orderId }}</span>
            <span
              class="ui-chip"
              :class="
                refund.status === 'pending'
                  ? 'ui-chip--warn'
                  : refund.status === 'approved'
                  ? 'ui-chip--ok'
                  : ''
              "
              >{{ refundStatus(refund.status) }}</span
            >
            <span class="fin-tx__net fin-num">{{ money(refund.amount) }}</span>
          </span>
          <span class="fin-tx__lines">
            <span
              >{{ $t("finance_reason") }} <b>{{ refund.reason || "—" }}</b></span
            >
            <span
              >{{ $t("finance_customer") }} <b>{{ refund.customer || "—" }}</b></span
            >
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import {
  MONEY_FLOW_META,
  REFUND_STATUS_META,
  formatMoney,
} from "@/components/restaurant/finance/constants";
import { formatShortDate } from "@/components/report/shared/periods";
import ReportChecklistMenu from "@/components/report/desktop/ReportChecklistMenu.vue";
import FinanceExport from "../parts/FinanceExport.vue";
import FinancePendingBar from "../parts/FinancePendingBar.vue";
import FinanceState from "../parts/FinanceState.vue";
import OrderPanel from "../panels/OrderPanel.vue";
import RefundPanel from "../panels/RefundPanel.vue";
import hasFinanceLabels from "../hasFinanceLabels";
import hasFinancePrint from "../hasFinancePrint";
import {
  ENTRY_TYPE_KEYS,
  PAYMENT_SOURCES,
  allowedFromError,
  entryTypeParam,
  groupTransactions,
  ledgerPayload,
  ledgerRows,
  mapTransaction,
  pageWindow,
  summaryFromTotals,
} from "../financeLogic";

const PAGE_SIZE = 100;

const mapRefund = (dto) => ({
  id: dto.id,
  orderId: dto.order_id,
  orderRef: dto.order_ref,
  customer: dto.customer,
  reason: dto.reason,
  amount: dto.amount,
  status: dto.status,
  approvedBy: dto.approved_by,
  comment: dto.comment,
  method: dto.method,
});

/**
 * Transakcije: "Po transakciji" (kartice narudžbi i događaja, zbir iz totals), "Izvod" (knjiga sa stanjem) i
 * "Refundacije" (zahtjevi). Pretraga ide na backend (?search=, po napomeni i broju narudžbe) u prva dva prikaza,
 * a klijentski samo u "Refundacijama". Stanje u izvodu je serverski `balance` po redu (ne računa se ovdje) i
 * pretraga ga ne mijenja. Stranice: ?page= + meta (current_page, last_page, total, per_page).
 * Kartica narudžbe otvara panel narudžbe sa zahtjevom za refundaciju.
 * Filteri "Po transakciji": način plaćanja (?payment_method=CASH|CARD) i tip stavke (?entry_type=A,B) rade na backendu nad
 * cijelim filtriranim skupom (meta, totals i stranice ih poštuju). Plaćanje isključuje stavke bez narudžbe (isplate, pretplate,
 * uplate). Tip stavke ostavlja kartici samo izabrane redove, pa se tada piše "zbir izabranih stavki", a ne neto narudžbe.
 * Nepoznat tip backend odbija sa 422 i poljem `allowed`: lista tipova se tada sama suzi na dozvoljene.
 */
export default {
  name: "FinanceTransactions",
  components: { FinanceExport, FinancePendingBar, FinanceState, ReportChecklistMenu },
  mixins: [hasFinanceLabels, hasFinancePrint],
  inject: { shell: "reportShell" },
  props: { period: { type: Object, required: true } },
  data: () => ({
    status: "loading",
    scope: "all",
    searchOpen: false,
    paymentMethod: "",
    // null = svi tipovi (bez filtera)
    entryTypes: null,
    // dozvoljeni tipovi iz 422 odgovora; null dok ih backend nije javio
    allowedTypes: null,
    serviceId: null,
    serviceTypes: [],
    search: "",
    rows: [],
    page: 1,
    lastPage: 1,
    total: 0,
    perPage: PAGE_SIZE,
    summary: null,
    opening: 0,
    entries: [],
    refunds: [],
    newOrderId: "",
    activeKey: "",
    searchTimer: null,
  }),
  computed: {
    printTitle() {
      return this.views.find((item) => item.key === this.view).label;
    },
    viewOptions() {
      return this.views.map((item) => ({
        value: item.key,
        label: item.label,
        badge: item.badge,
      }));
    },
    view() {
      return this.shell.txView;
    },
    loading() {
      return this.status === "loading";
    },
    failed() {
      return this.status === "error";
    },
    currency() {
      return (this.summary && this.summary.currency) || this.shell.currency;
    },
    views() {
      return [
        { key: "order", label: this.$t("finance_view_by_transaction") },
        { key: "ledger", label: this.$t("finance_view_statement") },
        {
          key: "refunds",
          label: this.$t("finance_nav_refunds"),
          badge: this.shell.pendingRefunds || "",
        },
      ];
    },
    serviceOptions() {
      return this.serviceTypes.map((st) => ({
        value: st.id,
        label: st.name || st.label || `#${st.id}`,
      }));
    },
    paymentOptions() {
      return [
        { value: "", label: this.$t("finance_shell.filters.payment_all") },
        { value: "CASH", label: this.$t("finance_payment_cash") },
        { value: "CARD", label: this.$t("finance_payment_card") },
      ];
    },
    /** tipovi koje nudimo: naših 12, a kad je backend javio `allowed`, samo ti (plus tipovi koje mi ne poznajemo) */
    entryTypeKeys() {
      if (!this.allowedTypes) return ENTRY_TYPE_KEYS;
      return [
        ...ENTRY_TYPE_KEYS.filter((key) => this.allowedTypes.includes(key)),
        ...this.allowedTypes.filter((key) => !ENTRY_TYPE_KEYS.includes(key)),
      ];
    },
    entryTypeItems() {
      return this.entryTypeKeys.map((value) => ({ value, text: this.typeText(value) }));
    },
    entryTypeValue() {
      const chosen = (this.entryTypes || []).filter((key) =>
        this.entryTypeKeys.includes(key)
      );
      return chosen.length ? chosen : this.entryTypeKeys;
    },
    entryTypeFiltered() {
      return entryTypeParam(this.entryTypes, this.entryTypeKeys) !== undefined;
    },
    filtersActive() {
      return Boolean(this.paymentMethod) || this.entryTypeFiltered;
    },
    // plaćanje i tip se mijenjaju zajedno (poništavanje): jedan ključ = jedan zahtjev
    filterKey() {
      return `${this.paymentMethod}|${
        entryTypeParam(this.entryTypes, this.entryTypeKeys) || ""
      }`;
    },
    groups() {
      return groupTransactions(this.rows);
    },
    visibleGroups() {
      if (this.scope === "orders") return this.groups.filter((g) => g.isOrder);
      if (this.scope === "payments")
        return this.groups.filter((g) => PAYMENT_SOURCES.includes(g.sourceType));
      return this.groups;
    },
    paymentCount() {
      return this.groups.filter((g) => PAYMENT_SOURCES.includes(g.sourceType)).length;
    },
    pageButtons() {
      return pageWindow(this.page, this.lastPage);
    },
    pageRange() {
      if (!this.total || !this.rows.length) return "";
      const from = (this.page - 1) * this.perPage + 1;
      return this.$t("finance_shell.page_range", {
        from,
        to: from + this.rows.length - 1,
        total: this.total,
      });
    },
    ledger() {
      return ledgerRows(this.entries);
    },
    visibleRefunds() {
      // zahtjevi se učitavaju svi odjednom pa se traži ovdje; customSearch izjednačava ćirilicu, latinicu i dijakritike
      const headers = ["orderId", "orderRef", "customer", "reason"].map((value) => ({
        value,
      }));
      return this.customSearch(this.refunds, headers, this.search);
    },
    phone() {
      return Boolean(this.shell.phone);
    },
    // na telefonu je polje za pretragu skriveno dok se ne dodirne lupa; dok upit postoji ostaje vidljivo (aktivan filter se ne krije)
    searchVisible() {
      return !this.phone || this.searchOpen || Boolean(this.search);
    },
    // alati za zaglavlje stranice na telefonu (lupa i izvoz); ljuska ih crta uz osvježi i štampu
    phoneTools() {
      if (!this.phone) return null;
      return {
        search: { on: this.searchVisible, toggle: this.toggleSearch },
        export: { filename: this.exportFilename, getData: this.getExportData, disabled: this.exportDisabled },
      };
    },
    validNewOrder() {
      return /^\d+$/.test(this.newOrderId.trim());
    },
    exportFilename() {
      return `transakcije_${this.period.from}_${this.period.to}`;
    },
    exportDisabled() {
      return this.view === "order"
        ? !this.visibleGroups.length
        : this.view === "ledger"
        ? !this.ledger.length
        : !this.visibleRefunds.length;
    },
    // from i to se mijenjaju zajedno: jedan ključ = jedan zahtjev (dva posebna watchera su slala dva)
    periodKey() {
      return `${this.period.from}|${this.period.to}`;
    },
  },
  watch: {
    periodKey: "reload",
    serviceId: "reload",
    view: "reload",
    filterKey() {
      // "Plaćanja" (isplate, uplate) nemaju način plaćanja: uz ovaj filter bi izbor uvijek bio prazan
      if (this.paymentMethod && this.scope === "payments") this.scope = "all";
      if (this.view === "order") this.reload();
    },
    phoneTools: {
      immediate: true,
      handler(tools) {
        this.shell.setScreenTools(this, tools);
      },
    },
    scope(value) {
      // isti razlog u suprotnom smjeru: izbor "Plaćanja" skida filter načina plaćanja (watcher filterKey ponovo učitava listu)
      if (value === "payments" && this.paymentMethod) this.paymentMethod = "";
    },
    search() {
      clearTimeout(this.searchTimer);
      // "Po transakciji" i "Izvod" traže na backendu (cijeli skup, ne samo učitanu stranicu); "Refundacije" su klijentski
      if (this.view === "refunds") return;
      this.searchTimer = setTimeout(
        () => (this.view === "ledger" ? this.loadLedger() : this.load(1)),
        400
      );
    },
  },
  created() {
    // brojač zahtjeva: odgovor starijeg zahtjeva (brzo kucanje, promjena prikaza) se odbacuje
    this.seq = 0;
    this.loadServiceTypes();
    this.reload();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.shell.clearScreenTools(this);
  },
  methods: {
    money(value, signed) {
      return formatMoney(value, this.currency, { signed });
    },
    flow(value) {
      return value ? MONEY_FLOW_META[value] || null : null;
    },
    dateTime(value) {
      if (!value) return "";
      const time = new Date(String(value).replace(" ", "T")).toLocaleTimeString("sr-RS", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${this.localeDate(value)} ${time}`;
    },
    shortDate(value) {
      return value ? formatShortDate(String(value).slice(0, 10)) : "";
    },
    eventTitle(group) {
      return group.eventTitleKey
        ? this.$t(group.eventTitleKey)
        : String(group.sourceType || "")
            .toLowerCase()
            .replace(/_/g, " ") || "—";
    },
    eventCaption(group) {
      return group.eventCaptionKey ? this.$t(group.eventCaptionKey) : "";
    },
    refundStatus(status) {
      const meta = REFUND_STATUS_META[status];
      return meta ? this.$t(meta.labelKey) : status;
    },
    /** naziv tipa stavke; tip bez prevoda se piše kao tekst koda ("loss compensation") */
    typeText(key) {
      const path = `finance_shell.types.${key}`;
      return this.$te(path)
        ? this.$t(path)
        : String(key).toLowerCase().replace(/_/g, " ");
    },
    setEntryTypes(next) {
      // sve izabrano ili ništa izabrano znači bez filtera
      this.entryTypes =
        next.length && next.length < this.entryTypeKeys.length ? next : null;
    },
    clearFilters() {
      this.paymentMethod = "";
      this.entryTypes = null;
    },
    /** 422 sa `allowed`: tipovi koje backend ne poznaje se izbacuju iz izbora i iz ponude; true kad je izbor promijenjen (watcher učitava ponovo) */
    adoptAllowedTypes(allowed) {
      this.allowedTypes = allowed;
      const kept = (this.entryTypes || []).filter((key) =>
        this.entryTypeKeys.includes(key)
      );
      const next = kept.length && kept.length < this.entryTypeKeys.length ? kept : null;
      const changed = JSON.stringify(next) !== JSON.stringify(this.entryTypes);
      if (changed) this.entryTypes = next;
      return changed;
    },
    chips(group) {
      const out = [];
      if (group.isOrder) {
        if (group.service) out.push({ text: group.service, tone: "" });
        if (group.hasCash) {
          out.push({
            text: `${this.$t("finance_payment_cash")} · ${
              group.advance
                ? this.$t("finance_cash_model_courier_pays")
                : this.$t("finance_line_customer_paid").toLowerCase()
            }`,
            tone: "",
          });
        } else if (group.paymentMethod === "CARD") {
          out.push({ text: this.$t("finance_payment_card"), tone: "" });
        } else if (group.paymentMethod === "CASH") {
          out.push({ text: this.$t("finance_payment_cash"), tone: "" });
        }
        if (group.hasRefund)
          out.push({
            text: this.$t("finance_chip_refund_approved"),
            tone: "ui-chip--blue",
          });
      } else if (group.note) {
        out.push({ text: group.note, tone: "" });
      }
      return out;
    },
    reload() {
      clearTimeout(this.searchTimer);
      if (this.view === "ledger") {
        this.entries = [];
        return this.loadLedger();
      }
      if (this.view === "refunds") return this.loadRefunds();
      return this.load(1);
    },
    async loadServiceTypes() {
      try {
        const { data } = await restaurantFinanceService.getServiceTypes();
        const list = Array.isArray(data) ? data : (data && data.data) || [];
        this.serviceTypes = list.filter(
          (st) => st && st.id !== null && st.id !== undefined
        );
      } catch (error) {
        this.serviceTypes = [];
      }
    },
    async load(page = 1) {
      const id = this.shell.restaurantId;
      if (!id) return;
      const seq = ++this.seq;
      this.status = "loading";
      try {
        const { data } = await restaurantFinanceService.getTransactions(id, {
          from: this.period.from,
          to: this.period.to,
          service_id: this.serviceId || undefined,
          payment_method: this.paymentMethod || undefined,
          entry_type: entryTypeParam(this.entryTypes, this.entryTypeKeys),
          search: this.search.trim() || undefined,
          per_page: PAGE_SIZE,
          page,
        });
        if (seq !== this.seq) return;
        const meta = (data && data.meta) || {};
        this.rows = ((data && data.data) || []).map(mapTransaction);
        this.page = meta.current_page || 1;
        this.lastPage = meta.last_page || 1;
        this.total = Number(meta.total) || this.rows.length;
        this.perPage = Number(meta.per_page) || PAGE_SIZE;
        // zbir za cijeli filtrirani skup (poštuje pretragu); bez totals-a zbir se ne prikazuje
        this.summary = summaryFromTotals(
          data && data.totals,
          (data && data.currency) || this.shell.currency
        );
        this.status = "ready";
      } catch (error) {
        if (seq !== this.seq) return;
        const response = error && error.response;
        const allowed =
          response && response.status === 422 && this.entryTypes
            ? allowedFromError(response.data)
            : null;
        if (allowed && this.adoptAllowedTypes(allowed)) {
          // izbor je promijenjen pa ga watcher (filterKey) ponovo učitava; ovdje samo obavijest
          this.shell.showToast(this.$t("finance_shell.filters.type_unsupported"));
          return;
        }
        this.status = "error";
      }
    },
    async loadLedger() {
      const id = this.shell.restaurantId;
      if (!id) return;
      const seq = ++this.seq;
      this.status = "loading";
      try {
        // search sužava samo prikazane redove; opening_balance i balance po redu backend računa nad punim skupom
        const { data } = await restaurantFinanceService.getBalanceLedger(id, {
          from: this.period.from,
          to: this.period.to,
          search: this.search.trim() || undefined,
        });
        if (seq !== this.seq) return;
        const payload = ledgerPayload(data);
        this.opening = payload.opening_balance || 0;
        this.entries = payload.ledger_entries || [];
        this.status = "ready";
      } catch (error) {
        if (seq === this.seq) this.status = "error";
      }
    },
    async loadRefunds() {
      const id = this.shell.restaurantId;
      if (!id) return;
      const seq = ++this.seq;
      this.status = "loading";
      try {
        const { data } = await restaurantFinanceService.getRefunds(id);
        if (seq !== this.seq) return;
        this.refunds = ((data && data.data) || []).map(mapRefund);
        this.shell.pendingRefunds = this.refunds.filter(
          (r) => r.status === "pending"
        ).length;
        this.status = "ready";
      } catch (error) {
        if (seq === this.seq) this.status = "error";
      }
    },
    /** Lupa na telefonu: otvara polje i fokusira ga; drugi dodir ga zatvara i briše upit (da filter ne ostane skriven). */
    toggleSearch() {
      if (this.searchVisible) {
        this.search = "";
        this.searchOpen = false;
        return;
      }
      this.searchOpen = true;
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },
    openOrder(group) {
      this.activeKey = group.key;
      this.shell.openPanel({
        key: `order-${group.sourceId}`,
        title: this.$t("finance_shell.order_title", { id: group.sourceId }),
        subtitle: this.dateTime(group.date),
        component: OrderPanel,
        // uz filter tipa kartica ima samo izabrane redove: panel ne smije iz njih računati "Neto za vas"
        props: {
          orderId: Number(group.sourceId),
          restaurantId: this.shell.restaurantId,
          currency: this.currency,
          group: this.entryTypeFiltered ? null : group,
        },
        root: true,
      });
    },
    newRefund() {
      if (!this.validNewOrder) return;
      const orderId = Number(this.newOrderId.trim());
      this.shell.openPanel({
        key: `order-${orderId}`,
        title: this.$t("finance_shell.order_title", { id: orderId }),
        component: OrderPanel,
        props: {
          orderId,
          restaurantId: this.shell.restaurantId,
          currency: this.currency,
        },
        root: true,
      });
    },
    openRefund(refund) {
      this.shell.openPanel({
        key: `refund-${refund.id}`,
        title: this.$t("finance_shell.refund.title", { id: refund.orderId }),
        component: RefundPanel,
        props: { refund, restaurantId: this.shell.restaurantId, currency: this.currency },
        root: true,
      });
    },
    /** CSV: redovi onako kako su na ekranu; štampa (forPrint) dodaje red "Početno stanje" na vrh izvoda. */
    getExportData(forPrint) {
      if (this.view === "ledger") {
        const rows = this.ledger.map((entry) => [
          entry.date,
          this.translate(entry.description),
          entry.amount,
          entry.balance === null ? "" : entry.balance,
        ]);
        if (forPrint)
          rows.unshift(["", this.$t("finance_opening_balance"), "", this.opening]);
        return {
          headers: [
            this.$t("finance_date"),
            this.$t("finance_description"),
            `${this.$t("finance_amount")} (${this.currency})`,
            this.$t("finance_balance"),
          ],
          rows,
          numeric: [2, 3],
        };
      }
      if (this.view === "refunds") {
        return {
          headers: [
            this.$t("finance_order"),
            this.$t("finance_customer"),
            this.$t("finance_reason"),
            `${this.$t("finance_amount")} (${this.currency})`,
            this.$t("finance_status_label"),
          ],
          rows: this.visibleRefunds.map((r) => [
            r.orderId,
            r.customer,
            r.reason,
            r.amount,
            this.refundStatus(r.status),
          ]),
          numeric: [3],
        };
      }
      return {
        headers: [
          this.$t("finance_order"),
          this.$t("finance_date"),
          this.$t("finance_description"),
          `${
            this.entryTypeFiltered
              ? this.$t("finance_shell.filters.selected_sum")
              : this.$t("finance_net")
          } (${this.currency})`,
        ],
        rows: this.visibleGroups.map((g) => [
          g.isOrder ? `#${g.sourceId}` : this.eventTitle(g),
          g.date,
          g.lines.map((l) => `${this.lineLabel(l)} ${l.signed.toFixed(2)}`).join(" | "),
          g.net.toFixed(2),
        ]),
        numeric: [3],
      };
    },
  },
};
</script>
